import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Col, Dropdown, Form, FormControl, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import {
  ArrowUpRightCircle,
  Bullseye,
  GraphUpArrow,
  People,
  PencilSquare,
  QuestionCircle,
  Search,
  Trash,
  X
} from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import './leadReport.scss';
import DropDownInput from '../../../form-generator/components/DropDownInput';
import { ABORT_ERROR, makeApiRequests } from '../../../helpers/api';
import { ENDPOINTS } from '../../../helpers/constants';
import {
  formatPhoneForTel,
  getLeadEmail,
  getLeadName,
  getExportedContractDisplayName,
  getExportedContractId,
  getLeadPhone,
  getTimelineBadgeVariant,
  parseDesiredMonthlyPayment
} from '../../../helpers/leadHelpers';
import { isAdmin } from '../../../helpers/roleHelpers';
import AlertModal from '../../AlertModal';
import { ContactEditSideBar } from '../../crm/SearchCrm';
import Loader from '../../Loader';
import AdvanceDateRangePicker from '../../common/AdvanceDateRangePicker';
import SearchableSelect from '../../common/SearchableSelect';
import SelectCheckbox from '../../common/SelectCheckbox';
import { useAdvancedDateRangePicker } from '../../../hooks/useAdvancedDateRangePicker';
import { useSelect } from '../../../hooks/useSelect';

const EXPORT_FILTERS = {
  ALL: 'all',
  EXPORTED: 'exported',
  NOT_EXPORTED: 'not_exported'
};

const TRAFFIC_FILTERS = {
  ALL: 'all',
  PAID: 'paid',
  ORGANIC: 'organic'
};

const LeadGeneratedAtCell = ({ value }) => {
  if (!value) return '—';
  const m = moment(value);
  if (!m.isValid()) return '—';
  return (
    <span className="lead-report__generated-at">
      <span className="lead-report__generated-date">{m.format('YYYY-MM-DD')}</span>
      <span className="lead-report__generated-time">{m.format('hh:mm A')}</span>
    </span>
  );
};

const formatDesiredMonthlyPaymentDisplay = value => {
  if (value === undefined || value === null || value === '') return '—';
  const parsed = parseDesiredMonthlyPayment(value);
  return parsed !== null ? `$${parsed}` : `$${value}`;
};

const getCleanPageUrl = rawUrl => {
  if (!rawUrl || typeof rawUrl !== 'string') return '—';
  const trimmed = rawUrl.trim();
  if (!trimmed) return '—';

  try {
    const parsed = new URL(trimmed);
    const clean = `${parsed.host}${parsed.pathname}`;
    return clean || '—';
  } catch (e) {
    const clean = trimmed
      .replace(/^https?:\/\//i, '')
      .split('?')[0]
      .split('#')[0];
    return clean || '—';
  }
};

const getPageUrlHref = rawUrl => {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  const trimmed = rawUrl.trim();
  if (!trimmed) return '';

  try {
    const parsed = new URL(trimmed);
    return parsed.toString();
  } catch (e) {
    try {
      return new URL(`https://${trimmed}`).toString();
    } catch (innerError) {
      return '';
    }
  }
};

const getContactOwnerOptions = appChoices => {
  return (
    appChoices
      .find(c => c.key === 'members')
      ?.values?.filter(m => {
        if (!m) return false;
        const lowered = [];
        if (Array.isArray(m.roles) && m.roles.length) {
          m.roles.forEach(r => lowered.push(String(r).toLowerCase()));
        }
        if (m.role) lowered.push(String(m.role).toLowerCase());
        return lowered.length > 0;
      }) || []
  ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

const SUMMARY_ACCENTS = {
  purple: { card: 'lead-report--accent-purple', icon: 'lead-report__icon-purple' },
  blue: { card: 'lead-report--accent-blue', icon: 'lead-report__icon-blue' },
  gold: { card: 'lead-report--accent-gold', icon: 'lead-report__icon-gold' }
};

const FilterField = ({ label, htmlFor, children }) => (
  <div className="lead-report__field">
    {label && (
      <Form.Label className="lead-report__field-label" htmlFor={htmlFor}>
        {label}
      </Form.Label>
    )}
    {children}
  </div>
);

const ToolbarFilter = ({ label, htmlFor, children, wide = false }) => (
  <div className={`lead-report__toolbar-filter${wide ? ' lead-report__toolbar-filter--search' : ''}`}>
    <label className="lead-report__toolbar-label" htmlFor={htmlFor}>
      {label}
    </label>
    {children}
  </div>
);

const FilterSelect = ({ id, value, options, optionValues, onChange, ariaLabel, isClearable = false }) => {
  const selectOptions = useMemo(
    () =>
      options.map((option, index) => ({
        label: option,
        value: optionValues ? optionValues[index] : option
      })),
    [options, optionValues]
  );

  return (
    <div className="lead-report__searchable-select-wrap">
      <SearchableSelect
        inputId={id}
        size="sm"
        options={selectOptions}
        value={value}
        onChange={nextValue => onChange(nextValue === '' || nextValue == null ? 'All' : nextValue)}
        placeholder={ariaLabel || 'Select...'}
        isClearable={isClearable}
        aria-label={ariaLabel || id}
        className="lead-report__searchable-select"
      />
    </div>
  );
};

const SUMMARY_CARD_HELP = {
  total: 'Total number of leads received from the website during the selected date range.',
  paidOrganic: 'Breakdown of leads acquired through Paid Ads and Organic traffic sources.',
  conversion:
    'Percentage of leads converted into contracts. Calculated as the ratio of leads exported to contracts to the total number of leads.'
};

const ExportedToCell = ({ exportedContract }) => {
  const contractId = getExportedContractId(exportedContract);
  const label = getExportedContractDisplayName(exportedContract);

  if (!contractId || !label) {
    return <span className="lead-report__exported-empty">—</span>;
  }

  return (
    <Link
      className="lead-report__exported-wrap"
      target="_blank"
      rel="noopener noreferrer"
      to={`/contract/${contractId}`}
      title={`Open contract: ${label}`}
      aria-label={`Open contract: ${label}`}
      onClick={e => e.stopPropagation()}
    >
      <span className="lead-report__exported-label">{label}</span>
      <ArrowUpRightCircle size={14} className="lead-report__exported-icon" aria-hidden />
    </Link>
  );
};

const DashboardSummaryCard = ({ accent, icon: Icon, title, children, onCardClick, helpText }) => {
  const accentClass = SUMMARY_ACCENTS[accent];

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onCardClick();
    }
  };

  return (
    <Col xs={12} sm={6} lg={4}>
      <div
        className={`lead-report__summary-card ${accentClass.card} lead-report__summary-card--clickable`}
        role="button"
        tabIndex={0}
        onClick={onCardClick}
        onKeyDown={handleKeyDown}
      >
        <div className="lead-report__summary-header">
          <Icon size={18} className={`lead-report__summary-icon ${accentClass.icon}`} />
          <span>{title}</span>
          {helpText && (
            <OverlayTrigger
              placement="auto"
              delay={{ show: 200, hide: 150 }}
              overlay={
                <Tooltip id={`lead-summary-tip-${accent}`} className="lead-report__summary-tooltip">
                  {helpText}
                </Tooltip>
              }
            >
              <span
                className="lead-report__summary-help"
                tabIndex={0}
                role="button"
                aria-label={`About ${title}`}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
              >
                <QuestionCircle size={14} aria-hidden />
              </span>
            </OverlayTrigger>
          )}
        </div>
        <div className="lead-report__summary-body">{children}</div>
      </div>
    </Col>
  );
};

const SummaryCardSkeleton = ({ accent }) => {
  const accentClass = SUMMARY_ACCENTS[accent] || SUMMARY_ACCENTS.purple;
  return (
    <Col xs={12} md={4}>
      <div className={`lead-report__summary-card ${accentClass.card}`}>
        <div className="lead-report__summary-header">
          <span className="lead-report__summary-skeleton lead-report__summary-skeleton--icon" />
          <span className="lead-report__summary-skeleton lead-report__summary-skeleton--title" />
        </div>
        <div className="lead-report__summary-body">
          <div className="lead-report__summary-skeleton lead-report__summary-skeleton--value" />
          <div className="lead-report__summary-skeleton lead-report__summary-skeleton--detail" />
        </div>
      </div>
    </Col>
  );
};

const EMPTY_SUMMARY = { total: 0, paid: 0, organic: 0, exported: 0, conversionRate: '0.0' };
const EMPTY_FILTER_OPTIONS = {
  moveTimelines: ['All'],
  creditSituations: ['All'],
  hasUnassignedOwner: false
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const LeadReport = ({ appChoices }) => {
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [exportFilter, setExportFilter] = useState(EXPORT_FILTERS.ALL);
  const [trafficFilter, setTrafficFilter] = useState(TRAFFIC_FILTERS.ALL);
  const [moveTimelineFilter, setMoveTimelineFilter] = useState('All');
  const [creditSituationFilter, setCreditSituationFilter] = useState('All');
  const [minPayment, setMinPayment] = useState('');
  const [maxPayment, setMaxPayment] = useState('');
  const [contactOwnerFilter, setContactOwnerFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [editingContact, setEditingContact] = useState(null);
  const [leadIdsToDelete, setLeadIdsToDelete] = useState([]);
  const [leadDeleting, setLeadDeleting] = useState(false);
  const [updatingIds, setUpdatingIds] = useState({});
  const canDeleteLeads = isAdmin();
  const {
    selectedIds,
    selectedCount,
    hasSelection,
    isSelected,
    allVisibleSelected,
    someVisibleSelected,
    toggle,
    toggleAll,
    clear,
    deselect
  } = useSelect(leads);
  const initialLeadDateRange = useMemo(
    () => ({
      startDate: moment()
        .startOf('month')
        .toDate(),
      endDate: moment()
        .endOf('month')
        .toDate(),
      key: 'selection',
      rangeType: 'This Month'
    }),
    []
  );
  const { dateRange, setDateRange, handleNavigateDate } = useAdvancedDateRangePicker(initialLeadDateRange);

  const statusOptions = useMemo(() => appChoices.find(ac => ac.key === 'Contact Status')?.values || [], [appChoices]);

  const contactOwnerMembers = useMemo(() => getContactOwnerOptions(appChoices), [appChoices]);

  const moveTimelineOptions = filterOptions.moveTimelines;
  const creditSituationOptions = filterOptions.creditSituations;

  const contactOwnerFilterOptions = useMemo(() => {
    const options = [{ label: 'All', value: 'All' }];
    contactOwnerMembers.forEach(m => {
      if (m?._id) options.push({ label: m.name, value: m._id });
    });
    if (filterOptions.hasUnassignedOwner) {
      options.push({ label: 'Unassigned', value: 'unassigned' });
    }
    return options;
  }, [contactOwnerMembers, filterOptions.hasUnassignedOwner]);

  const fetchAbortRef = useRef(null);
  const lastSummaryDateKeyRef = useRef(null);

  const dateRangeKey = useMemo(
    () =>
      `${
        dateRange.startDate
          ? moment(dateRange.startDate)
              .startOf('day')
              .valueOf()
          : ''
      }-${
        dateRange.endDate
          ? moment(dateRange.endDate)
              .endOf('day')
              .valueOf()
          : ''
      }`,
    [dateRange.startDate, dateRange.endDate]
  );

  const debouncedSetSearch = useRef(
    debounce(value => {
      setDebouncedSearchQuery(value);
    }, 300)
  ).current;

  useEffect(() => {
    debouncedSetSearch(searchQuery.trim());
    return () => debouncedSetSearch.cancel();
  }, [searchQuery, debouncedSetSearch]);

  const fetchLeads = useCallback(async () => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    const dateChanged = lastSummaryDateKeyRef.current !== dateRangeKey;
    if (dateChanged) {
      setSummaryLoading(true);
      lastSummaryDateKeyRef.current = dateRangeKey;
    }
    setTableLoading(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_SEARCH_WEBSITE_LEADS,
      requestBody: {
        datePreset: 'custom',
        startDate: dateRange.startDate
          ? moment(dateRange.startDate)
              .startOf('day')
              .toISOString()
          : undefined,
        endDate: dateRange.endDate
          ? moment(dateRange.endDate)
              .endOf('day')
              .toISOString()
          : undefined,
        searchQuery: debouncedSearchQuery,
        exportFilter,
        trafficFilter,
        moveTimelineFilter,
        creditSituationFilter,
        minPayment: minPayment !== '' ? minPayment : undefined,
        maxPayment: maxPayment !== '' ? maxPayment : undefined,
        contactOwnerFilter,
        page,
        limit
      },
      signal: controller.signal
    });

    if (error === ABORT_ERROR) return;

    setSummaryLoading(false);
    setTableLoading(false);

    if (error) {
      toast.error(error);
      return;
    }
    setLeads(response?.results || []);
    clear();
    setSummary(response?.summary || EMPTY_SUMMARY);
    setFilterOptions(response?.filterOptions || EMPTY_FILTER_OPTIONS);
    setPagination(response?.pagination || { page: 1, limit, total: 0, totalPages: 1 });
  }, [
    dateRangeKey,
    dateRange.startDate,
    dateRange.endDate,
    debouncedSearchQuery,
    exportFilter,
    trafficFilter,
    moveTimelineFilter,
    creditSituationFilter,
    minPayment,
    maxPayment,
    contactOwnerFilter,
    page,
    limit,
    clear
  ]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    return () => {
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
    };
  }, []);

  // Reset to page 1 whenever a filter changes (not when page itself changes)
  const setFilterAndResetPage = setter => value => {
    setPage(1);
    setter(value);
  };

  const resetAllFilters = () => {
    setPage(1);
    setSearchQuery('');
    setContactOwnerFilter('All');
    setExportFilter(EXPORT_FILTERS.ALL);
    setTrafficFilter(TRAFFIC_FILTERS.ALL);
    setMoveTimelineFilter('All');
    setCreditSituationFilter('All');
    setMinPayment('');
    setMaxPayment('');
  };

  const applySummaryPaidFilter = () => {
    setPage(1);
    setExportFilter(EXPORT_FILTERS.ALL);
    setTrafficFilter(TRAFFIC_FILTERS.PAID);
  };

  const applySummaryExportedFilter = () => {
    setPage(1);
    setTrafficFilter(TRAFFIC_FILTERS.ALL);
    setExportFilter(EXPORT_FILTERS.EXPORTED);
  };

  const hasMoreFiltersActive = minPayment !== '' || maxPayment !== '';

  const clearMoreFilters = () => {
    setPage(1);
    setMinPayment('');
    setMaxPayment('');
  };

  const moreFiltersCount = [minPayment !== '', maxPayment !== ''].filter(Boolean).length;

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (searchQuery.trim()) {
      chips.push({ key: 'search', label: `"${searchQuery.trim()}"`, onRemove: () => setSearchQuery('') });
    }
    if (contactOwnerFilter !== 'All') {
      const owner = contactOwnerFilterOptions.find(o => o.value === contactOwnerFilter);
      chips.push({ key: 'owner', label: owner?.label || 'Owner', onRemove: () => setContactOwnerFilter('All') });
    }
    if (exportFilter === EXPORT_FILTERS.EXPORTED) {
      chips.push({ key: 'export', label: 'Exported', onRemove: () => setExportFilter(EXPORT_FILTERS.ALL) });
    }
    if (exportFilter === EXPORT_FILTERS.NOT_EXPORTED) {
      chips.push({ key: 'export', label: 'Not exported', onRemove: () => setExportFilter(EXPORT_FILTERS.ALL) });
    }
    if (trafficFilter === TRAFFIC_FILTERS.PAID) {
      chips.push({ key: 'traffic', label: 'Paid', onRemove: () => setTrafficFilter(TRAFFIC_FILTERS.ALL) });
    }
    if (trafficFilter === TRAFFIC_FILTERS.ORGANIC) {
      chips.push({ key: 'traffic', label: 'Organic', onRemove: () => setTrafficFilter(TRAFFIC_FILTERS.ALL) });
    }
    if (moveTimelineFilter !== 'All') {
      chips.push({ key: 'timeline', label: moveTimelineFilter, onRemove: () => setMoveTimelineFilter('All') });
    }
    if (creditSituationFilter !== 'All') {
      chips.push({ key: 'credit', label: creditSituationFilter, onRemove: () => setCreditSituationFilter('All') });
    }
    if (minPayment !== '') {
      chips.push({ key: 'minPay', label: `Min $${minPayment}`, onRemove: () => setMinPayment('') });
    }
    if (maxPayment !== '') {
      chips.push({ key: 'maxPay', label: `Max $${maxPayment}`, onRemove: () => setMaxPayment('') });
    }
    return chips;
  }, [
    searchQuery,
    contactOwnerFilter,
    contactOwnerFilterOptions,
    exportFilter,
    trafficFilter,
    moveTimelineFilter,
    creditSituationFilter,
    minPayment,
    maxPayment
  ]);

  const updateLeadInList = updatedLead => {
    setLeads(prev =>
      prev.map(l => {
        if (l._id !== updatedLead._id) return l;
        return { ...updatedLead, trafficType: l.trafficType };
      })
    );
  };

  const patchLead = async (leadId, payload) => {
    setUpdatingIds(prev => ({ ...prev, [leadId]: true }));
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_WITH_ID(leadId),
      method: 'PUT',
      requestBody: payload
    });
    setUpdatingIds(prev => ({ ...prev, [leadId]: false }));

    if (error) {
      toast.error(error);
      return null;
    }

    updateLeadInList(response);
    return response;
  };

  const onInstantFieldChange = async (lead, field, value) => {
    if (!value || value === 'dropdown-placeholder' || lead[field] === value) return;
    await patchLead(lead._id, { [field]: value });
  };

  const onContactOwnerChange = async (lead, ownerId) => {
    if (ownerId === 'dropdown-placeholder') return;
    const currentOwnerId = lead.contactOwner?._id || '';
    if ((ownerId || '') === currentOwnerId) return;
    await patchLead(lead._id, { contactOwner: ownerId || null });
  };

  const tableColumnCount = canDeleteLeads ? 15 : 14;
  const isBulkDelete = leadIdsToDelete.length > 1;

  const deleteLeads = async () => {
    if (!leadIdsToDelete.length) return;

    setLeadDeleting(true);

    const { error } =
      leadIdsToDelete.length === 1
        ? await makeApiRequests({
            method: 'DELETE',
            endpoint: ENDPOINTS.CONTACTS_WITH_ID(leadIdsToDelete[0])
          })
        : await makeApiRequests({
            method: 'DELETE',
            endpoint: ENDPOINTS.CONTACTS_BULK_DELETE,
            requestBody: { contactIds: leadIdsToDelete }
          });

    setLeadDeleting(false);

    if (error) {
      toast.error(error);
      return;
    }

    toast.success(leadIdsToDelete.length === 1 ? 'Lead deleted successfully!' : 'Leads deleted successfully!');
    setLeadIdsToDelete([]);
    clear();
    fetchLeads();
  };

  const renderPhoneCell = lead => {
    const phone = getLeadPhone(lead);
    if (!phone) return '—';
    const tel = formatPhoneForTel(phone);
    return tel ? (
      <a href={`tel:${tel}`} onClick={e => e.stopPropagation()}>
        {phone}
      </a>
    ) : (
      phone
    );
  };

  return (
    <div className="lead-report">
      <div className="lead-report__page-header">
        <h1 className="lead-report__title">Leads Report</h1>
        <div className="lead-report__date-filter-row">
          <AdvanceDateRangePicker
            value={dateRange}
            onChange={ranges => {
              setPage(1);
              setDateRange(ranges.selection);
            }}
            showNavigator
            rangeType={dateRange.rangeType}
            handleNavigateDate={(rangeType, direction) => {
              setPage(1);
              handleNavigateDate(rangeType, direction);
            }}
          />
        </div>
      </div>

      <Row className="mb-2" style={{ rowGap: '12px' }}>
        {summaryLoading ? (
          <>
            <SummaryCardSkeleton accent="purple" />
            <SummaryCardSkeleton accent="blue" />
            <SummaryCardSkeleton accent="gold" />
          </>
        ) : (
          <>
            <DashboardSummaryCard
              accent="purple"
              icon={People}
              title="Total Leads"
              helpText={SUMMARY_CARD_HELP.total}
              onCardClick={resetAllFilters}
            >
              <div className="lead-report__summary-value">{summary.total}</div>
              <div className="lead-report__summary-detail">Leads in selected period</div>
            </DashboardSummaryCard>
            <DashboardSummaryCard
              accent="blue"
              icon={Bullseye}
              title="Paid vs. Organic"
              helpText={SUMMARY_CARD_HELP.paidOrganic}
              onCardClick={applySummaryPaidFilter}
            >
              <div className="lead-report__summary-row">
                <span>Paid</span>
                <strong>{summary.paid}</strong>
              </div>
              <div className="lead-report__summary-row">
                <span>Organic</span>
                <strong>{summary.organic}</strong>
              </div>
            </DashboardSummaryCard>
            <DashboardSummaryCard
              accent="gold"
              icon={GraphUpArrow}
              title="Conversion / Export Rate"
              helpText={SUMMARY_CARD_HELP.conversion}
              onCardClick={applySummaryExportedFilter}
            >
              <div className="lead-report__summary-value">
                {summary.exported} <span className="lead-report__summary-detail">({summary.conversionRate}%)</span>
              </div>
              <div className="lead-report__summary-detail">Leads exported to contract</div>
            </DashboardSummaryCard>
          </>
        )}
      </Row>

      <div className="lead-report__toolbar mb-2">
        <div className="lead-report__toolbar-row">
          <ToolbarFilter label="Search" htmlFor="leadGlobalSearch" wide>
            <div className="lead-report__search-wrap">
              <Search className="lead-report__search-icon" size={16} aria-hidden />
              <FormControl
                id="leadGlobalSearch"
                className="lead-report__control lead-report__control--search"
                placeholder="Name, email, phone, or ID..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </ToolbarFilter>

          <ToolbarFilter label="Contact Owner" htmlFor="contactOwnerFilter">
            <FilterSelect
              id="contactOwnerFilter"
              ariaLabel="Contact owner"
              value={contactOwnerFilter}
              options={contactOwnerFilterOptions.map(o => o.label)}
              optionValues={contactOwnerFilterOptions.map(o => o.value)}
              onChange={setFilterAndResetPage(setContactOwnerFilter)}
            />
          </ToolbarFilter>

          <ToolbarFilter label="Traffic Type" htmlFor="trafficFilter">
            <FilterSelect
              id="trafficFilter"
              ariaLabel="Traffic type"
              value={trafficFilter}
              options={['All', 'Paid', 'Organic']}
              optionValues={[TRAFFIC_FILTERS.ALL, TRAFFIC_FILTERS.PAID, TRAFFIC_FILTERS.ORGANIC]}
              onChange={setFilterAndResetPage(setTrafficFilter)}
            />
          </ToolbarFilter>

          <ToolbarFilter label="Export Status" htmlFor="exportFilter">
            <FilterSelect
              id="exportFilter"
              ariaLabel="Export status"
              value={exportFilter}
              options={['All', 'Exported', 'Not exported']}
              optionValues={[EXPORT_FILTERS.ALL, EXPORT_FILTERS.EXPORTED, EXPORT_FILTERS.NOT_EXPORTED]}
              onChange={setFilterAndResetPage(setExportFilter)}
            />
          </ToolbarFilter>

          <ToolbarFilter label="Move Timeline" htmlFor="moveTimelineFilter">
            <FilterSelect
              id="moveTimelineFilter"
              ariaLabel="Move timeline"
              value={moveTimelineFilter}
              options={moveTimelineOptions}
              onChange={setFilterAndResetPage(setMoveTimelineFilter)}
            />
          </ToolbarFilter>

          <ToolbarFilter label="Credit Situation" htmlFor="creditFilter">
            <FilterSelect
              id="creditFilter"
              ariaLabel="Credit situation"
              value={creditSituationFilter}
              options={creditSituationOptions}
              onChange={setFilterAndResetPage(setCreditSituationFilter)}
            />
          </ToolbarFilter>

          <ToolbarFilter label="Payment" htmlFor="lead-more-filters">
            <Dropdown align="end" className="lead-report__more-filters">
              <Dropdown.Toggle
                variant="light"
                id="lead-more-filters"
                className={`lead-report__more-toggle ${hasMoreFiltersActive ? 'lead-report__more-toggle--active' : ''}`}
              >
                {hasMoreFiltersActive ? `${moreFiltersCount} set` : 'Payment'}
              </Dropdown.Toggle>
              <Dropdown.Menu className="lead-report__more-menu" onClick={e => e.stopPropagation()}>
                <div className="lead-report__more-header">
                  <span className="lead-report__more-title">Monthly payment</span>
                  <span className="lead-report__more-subtitle">Filter by desired payment range</span>
                </div>
                <div className="lead-report__more-body">
                  <div className="lead-report__more-grid lead-report__more-grid--payment">
                    <FilterField label="Min ($)" htmlFor="minPaymentPopover">
                      <FormControl
                        id="minPaymentPopover"
                        className="lead-report__control"
                        type="number"
                        min="0"
                        placeholder="Min"
                        value={minPayment}
                        onChange={e => setMinPayment(e.target.value)}
                      />
                    </FilterField>
                    <FilterField label="Max ($)" htmlFor="maxPaymentPopover">
                      <FormControl
                        id="maxPaymentPopover"
                        className="lead-report__control"
                        type="number"
                        min="0"
                        placeholder="Max"
                        value={maxPayment}
                        onChange={e => setMaxPayment(e.target.value)}
                      />
                    </FilterField>
                  </div>
                </div>
                {hasMoreFiltersActive && (
                  <div className="lead-report__more-footer">
                    <button type="button" className="lead-report__more-clear" onClick={clearMoreFilters}>
                      Clear payment range
                    </button>
                  </div>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </ToolbarFilter>

          <button type="button" className="lead-report__reset-btn" onClick={resetAllFilters}>
            Reset all
          </button>
        </div>

        {activeFilterChips.length > 0 && (
          <div className="lead-report__active-filters">
            <span className="lead-report__active-label">Active filters:</span>
            {activeFilterChips.map((chip, chipIndex) => (
              <button
                key={`${chip.key}-${chipIndex}`}
                type="button"
                className="lead-report__filter-chip"
                onClick={chip.onRemove}
              >
                {chip.label}
                <X size={12} aria-hidden />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="lead-report__table-card mb-3">
        {canDeleteLeads && !tableLoading && hasSelection && (
          <div className="lead-report__bulk-bar">
            <span className="lead-report__bulk-count">
              {selectedCount} selected
            </span>
            <button
              type="button"
              className="lead-report__bulk-delete"
              title="Delete selected leads"
              aria-label="Delete selected leads"
              onClick={() => setLeadIdsToDelete([...selectedIds])}
            >
              <Trash size={16} aria-hidden /> Delete Selected
            </button>
          </div>
        )}
        <div className="lead-report__table-body">
          {tableLoading ? (
            <Loader />
          ) : (
            <>
              <div className="table-responsive">
                <Table hover size="sm" className="mb-0 lead-report__table">
                  <thead>
                    <tr>
                      {canDeleteLeads && (
                        <th className="lead-report__checkbox-cell">
                          <SelectCheckbox
                            checked={allVisibleSelected}
                            indeterminate={someVisibleSelected}
                            onChange={toggleAll}
                            ariaLabel="Select all leads on this page"
                            className="lead-report__checkbox"
                          />
                        </th>
                      )}
                      <th>Date Generated</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Exported To</th>
                      <th>Traffic</th>
                      <th>Page URL</th>
                      <th>Status</th>
                      <th>Timeline</th>
                      <th>Credit</th>
                      <th>Max Payment</th>
                      <th>Personal Notes</th>
                      <th>Contact Owner</th>
                      <th className="lead-report__actions-cell">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.length === 0 ? (
                      <tr>
                        <td colSpan={tableColumnCount} className="text-center text-muted py-4">
                          No leads match your filters.
                        </td>
                      </tr>
                    ) : (
                      leads.map(lead => {
                        const wfd = lead.webhookFormData || {};
                        const trafficType = lead.trafficType || 'Organic';
                        const isRowUpdating = updatingIds[lead._id];
                        const isRowSelected = isSelected(lead._id);

                        return (
                          <tr key={lead._id} className={isRowSelected ? 'lead-report__row--selected' : undefined}>
                            {canDeleteLeads && (
                              <td
                                className="lead-report__checkbox-cell"
                                onClick={e => e.stopPropagation()}
                              >
                                <SelectCheckbox
                                  checked={isRowSelected}
                                  onChange={() => toggle(lead._id)}
                                  ariaLabel={`Select ${getLeadName(lead) || 'lead'}`}
                                  className="lead-report__checkbox"
                                />
                              </td>
                            )}
                            <td className="lead-report__date-cell">
                              <LeadGeneratedAtCell value={wfd.leadGeneratedAt} />
                            </td>
                            <td style={{ fontWeight: 700, color: '#000' }}>{getLeadName(lead) || '—'}</td>
                            <td>{getLeadEmail(lead) || '—'}</td>
                            <td>{renderPhoneCell(lead)}</td>
                            <td className="lead-report__exported-cell">
                              <ExportedToCell exportedContract={lead.exportedContract} />
                            </td>
                            <td>
                              <Badge variant={trafficType === 'Paid' ? 'primary' : 'success'}>{trafficType}</Badge>
                              {typeof wfd.utm_source === 'string' && <div className="small">Source: {wfd.utm_source}</div>}
                              {typeof wfd.utm_campaign === 'string' && <div className="small">Campaign: {wfd.utm_campaign}</div>}
                              {typeof wfd.utm_content === 'string' && <div className="small">Post: {wfd.utm_content}</div>}
                              {typeof wfd.referral_partner === 'string' && <div className="small">Partner: {wfd.referral_partner}</div>}
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              {getPageUrlHref(wfd.pageUrl) ? (
                                <a href={getPageUrlHref(wfd.pageUrl)} target="_blank" rel="noopener noreferrer">
                                  {getCleanPageUrl(wfd.pageUrl)}
                                </a>
                              ) : (
                                getCleanPageUrl(wfd.pageUrl)
                              )}
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              <DropDownInput
                                id={`status-${lead._id}`}
                                size="sm"
                                required
                                disabled={isRowUpdating}
                                value={lead.status}
                                options={statusOptions}
                                onChangeFunction={e =>
                                  e?.target && onInstantFieldChange(lead, 'status', e.target.value)
                                }
                              />
                            </td>
                            <td>
                              {wfd.moveTimeline ? (
                                <Badge variant={getTimelineBadgeVariant(wfd.moveTimeline)}>{wfd.moveTimeline}</Badge>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td>{wfd.creditSituation || '—'}</td>
                            <td>{formatDesiredMonthlyPaymentDisplay(wfd.desiredMonthlyPayment)}</td>
                            <td>{lead.personalNotes || '—'}</td>
                            <td onClick={e => e.stopPropagation()}>
                              <DropDownInput
                                id={`owner-${lead._id}`}
                                size="sm"
                                disabled={isRowUpdating}
                                value={lead.contactOwner?._id || ''}
                                options={['', ...contactOwnerMembers.map(m => m.name)]}
                                optionValues={['', ...contactOwnerMembers.map(m => m._id)]}
                                onChangeFunction={e => e?.target && onContactOwnerChange(lead, e.target.value)}
                              />
                            </td>
                            <td className="lead-report__actions-cell" onClick={e => e.stopPropagation()}>
                              <div className="lead-report__actions-icons">
                                <PencilSquare
                                  className="lead-report__actions-icon text-primary"
                                  title="Edit lead"
                                  onClick={() => setEditingContact(lead)}
                                />
                                {canDeleteLeads && (
                                  <Trash
                                    className="lead-report__actions-icon text-danger"
                                    title="Delete lead"
                                    onClick={() => setLeadIdsToDelete([lead._id])}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
              {pagination.totalPages > 0 && (
                <div className="lead-report__pagination">
                  <span className="lead-report__pagination-info">
                    {pagination.total === 0
                      ? 'No results'
                      : `Showing ${(pagination.page - 1) * pagination.limit + 1}–${Math.min(
                          pagination.page * pagination.limit,
                          pagination.total
                        )} of ${pagination.total}`}
                  </span>

                  <div className="lead-report__pagination-controls">
                    <button
                      type="button"
                      className="lead-report__pagination-btn"
                      disabled={pagination.page <= 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Previous
                    </button>
                    <span className="lead-report__pagination-page">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      type="button"
                      className="lead-report__pagination-btn"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </button>
                  </div>

                  <div className="lead-report__pagination-size">
                    <label htmlFor="leadPageSize" className="lead-report__toolbar-label">
                      Per page
                    </label>
                    <FilterSelect
                      id="leadPageSize"
                      ariaLabel="Per page"
                      value={limit}
                      options={PAGE_SIZE_OPTIONS.map(String)}
                      optionValues={PAGE_SIZE_OPTIONS}
                      onChange={value => {
                        setPage(1);
                        setLimit(Number(value));
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ContactEditSideBar
        editingContact={editingContact}
        onContactEditSideBarHide={() => setEditingContact(null)}
        appChoices={appChoices}
        onContactDelete={contactId => {
          setLeads(prev => prev.filter(l => l._id !== contactId));
          deselect(contactId);
          setEditingContact(null);
          fetchLeads();
        }}
        onContactEdit={updated => {
          updateLeadInList(updated);
          setEditingContact(null);
        }}
      />

      <AlertModal
        alertText={
          isBulkDelete
            ? `Are you sure you want to delete ${leadIdsToDelete.length} leads? This action cannot be undone!`
            : 'Are you sure you want to delete this lead? This action cannot be undone!'
        }
        show={leadIdsToDelete.length > 0}
        onHide={() => !leadDeleting && setLeadIdsToDelete([])}
        continueButtonText={isBulkDelete ? 'Delete selected' : 'Delete'}
        continueButtonVariant="danger"
        onContinueClick={deleteLeads}
        progressText={isBulkDelete ? 'Deleting leads...' : 'Deleting lead...'}
        showProgress={leadDeleting}
        onDismissClick={() => !leadDeleting && setLeadIdsToDelete([])}
      />
    </div>
  );
};

export default LeadReport;
